/*
 * NegoStore: filters panel.
 *
 * Drop-in replacement for <modal-dialog> around the collection filters form.
 * ModalDialog relocates itself to <body> on connect, which makes it impossible
 * to keep the form inside the left column of .collection-wrapper. This element
 * never moves, so the same markup can be a drawer below the breakpoint and a
 * plain sidebar above it.
 */

const FACETS_PANEL_BREAKPOINT = 990;

class FacetsPanel extends HTMLElement {
  constructor() {
    super();

    const breakpoint =
      parseInt(this.dataset.breakpoint, 10) || FACETS_PANEL_BREAKPOINT;
    this.mediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);

    this.handleHide = () => this.hide();
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
  }

  connectedCallback() {
    if (!this.hasSetup) {
      this.hasSetup = true;

      this.addEventListener("keyup", (event) => {
        if (event.code && event.code.toUpperCase() === "ESCAPE") this.hide();
      });

      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }

    this.initCloseButtons();
    this.syncToBreakpoint();

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.onBreakpointChange);
    } else {
      this.mediaQuery.addListener(this.onBreakpointChange);
    }
  }

  disconnectedCallback() {
    // Daca sectiunea e reincarcata (editorul de teme) cu sertarul deschis,
    // <body> ar ramane cu clasele puse: header invizibil si bula de chat
    // moarta pana la un reload complet.
    this.hide(true);

    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener("change", this.onBreakpointChange);
    } else {
      this.mediaQuery.removeListener(this.onBreakpointChange);
    }
  }

  get isSidebarLayout() {
    return this.classList.contains("facets-panel--sidebar");
  }

  // Everything below the breakpoint is a drawer, and so is the whole "popup"
  // layout. Scroll lock, focus trap and overlay only ever apply here.
  get isDrawer() {
    return !(this.isSidebarLayout && this.mediaQuery.matches);
  }

  initCloseButtons() {
    const closeBtn = this.querySelector('[id^="ModalClose-"]');
    if (!closeBtn) return;

    closeBtn.removeEventListener("click", this.handleHide);
    closeBtn.addEventListener("click", this.handleHide);
  }

  onBreakpointChange() {
    this.syncToBreakpoint();
  }

  syncToBreakpoint() {
    if (!this.isSidebarLayout || !this.mediaQuery.matches) return;

    // Drop the drawer state before the panel becomes a static column,
    // otherwise <body> would keep overflow-hidden with no way to close.
    this.hide(true);
  }

  show(opener) {
    if (this.classList.contains("hiding") || !this.isDrawer) return;

    this.openedBy = opener;
    document.body.classList.add("overflow-hidden");
    // Steag propriu, nu "overflow-hidden": ala e pus si de alte modale
    // (details-modal, cautare), iar headerul nu trebuie ascuns la toate.
    document.body.classList.add("nego-facets-open");
    this.setAttribute("open", "");
    this.initCloseButtons();

    this.addEventListener(
      "transitionend",
      () => {
        trapFocus(this, this.querySelector('[role="dialog"]'));
      },
      { once: true },
    );
  }

  hide(instant = false) {
    // Curatarea de pe <body> se face INAINTE de garda: daca atributul open a
    // disparut altfel decat prin hide(), clasele tot trebuie sa plece.
    // Conditionata pe clasa noastra, pentru ca "overflow-hidden" e pusa si de
    // alte modale — n-avem voie sa deblocam scroll-ul in locul lor.
    if (document.body.classList.contains("nego-facets-open")) {
      document.body.classList.remove("overflow-hidden");
      document.body.classList.remove("nego-facets-open");
    }

    if (!this.hasAttribute("open")) return;

    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);

    if (instant) return;

    this.classList.add("hiding");
    setTimeout(() => this.classList.remove("hiding"), 700);
  }
}

customElements.define("facets-panel", FacetsPanel);

class FacetsOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");
    if (!button) return;

    const panelOf = () =>
      document.querySelector(this.getAttribute("data-modal"));

    let armed = false;

    /* Ascunderea headerului porneste de la ATINGERE, nu de la click.
       Pe telefon, intre pointerdown si click trec zeci-sute de ms (browserul
       asteapta sa vada daca e tap sau scroll), iar in tot acest timp logoul
       ramane pe ecran. Clasa e aceeasi pe care o pune si show(), deci a doua
       adaugare nu face nimic. */
    button.addEventListener("pointerdown", () => {
      if (!panelOf()) return;
      armed = true;
      document.body.classList.add("nego-facets-open");
    });

    /* Daca gestul nu se termina in deschidere, headerul trebuie pus la loc.
       pointercancel inseamna ca browserul a luat gestul pe cont propriu (scroll):
       click nu mai vine niciodata, deci curatam imediat.
       Dupa pointerup asteptam 600ms, nu un tick: `click` vine DUPA pointerup, iar
       pe telefon poate intarzia. Cu setTimeout(0) am fi sters clasa inaintea
       clicului, show() ar fi pus-o la loc, si exact asta ar fi produs clipirea
       pe care incercam sa o eliminam. */
    const disarm = () => {
      if (!armed) return;
      armed = false;
      const panel = panelOf();
      if (!panel || !panel.hasAttribute("open")) {
        document.body.classList.remove("nego-facets-open");
      }
    };
    button.addEventListener("pointercancel", disarm);
    button.addEventListener("pointerup", () => setTimeout(disarm, 600));

    button.addEventListener("click", () => {
      const panel = panelOf();
      if (panel) panel.show(button);
    });
  }
}

customElements.define("facets-opener", FacetsOpener);
